import React, { useState, useEffect } from 'react';
import './setPriceMargin.css';

/* ── Timeline View ── */
const TimelineView = ({ sortedHistory, formatDate }) => (
  <div className="spm-timeline">
    {sortedHistory.map((row, idx) => (
      <div className="spm-tl-item" key={row.id ?? idx}>
        <div className="spm-tl-dot" />
        <div className="spm-tl-card">

          {/* ID Badge */}
          <div className="spm-tl-id-badge">
            <span className="spm-tl-id-label">#ID</span>
            <span>{row.id}</span>
          </div>

          {/* Margin + bar */}
          <div className="spm-tl-margin-block">
            <div className="spm-tl-margin-top">
              <span className="spm-tl-margin-val">{row.newMargin}</span>
              <span className="spm-tl-margin-pct">%</span>
              {idx === 0 && <span className="spm-latest-chip">● Latest</span>}
            </div>
            <div className="spm-tl-bar-track">
              <div
                className="spm-tl-bar-fill"
                style={{ width: `${Math.min(row.newMargin, 100)}%` }}
              />
            </div>
          </div>

          {/* Date */}
          <div className="spm-tl-date">
            <span className="spm-tl-date-icon">🗓</span>
            <span className="spm-tl-date-val">{formatDate(row.updatedAt)}</span>
          </div>

        </div>
      </div>
    ))}
  </div>
);

/* ── Table View ── */
const TableView = ({ sortedHistory, formatDate }) => (
  <div className="spm-table-wrap">
    <table className="spm-table">
      <thead>
        <tr>
          <th>Margin ID</th>
          <th>New Margin</th>
          <th>Visual</th>
          <th>Date &amp; Time</th>
        </tr>
      </thead>
      <tbody>
        {sortedHistory.map((row, idx) => (
          <tr key={row.id ?? idx} className={idx === 0 ? 'spm-row-latest' : ''}>
            <td className="spm-td-idx">
              #{row.id}
              {idx === 0 && (
                <span className="spm-latest-chip" style={{ marginLeft: 8 }}>Latest</span>
              )}
            </td>
            <td>
              <span className="spm-margin-chip spm-chip-red">{row.newMargin}%</span>
            </td>
            <td className="spm-td-bar">
              <div className="spm-td-bar-track">
                <div
                  className="spm-td-bar-fill"
                  style={{ width: `${Math.min(row.newMargin, 100)}%` }}
                />
              </div>
            </td>
            <td className="spm-td-date">{formatDate(row.updatedAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SetPriceMargin = () => {
  const [currentMargin, setCurrentMargin] = useState(null);
  const [newMargin, setNewMargin]         = useState('');
  const [marginLoading, setMarginLoading] = useState(true);
  const [marginUpdatedDate, setMarginUpdatedDate] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [saving, setSaving]               = useState(false);
  const [loading, setLoading]             = useState(true);
  const [history, setHistory]             = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [error, setError]                 = useState(null);
  const [successMsg, setSuccessMsg]       = useState('');
  const [inputError, setInputError]       = useState('');
  const [historyView, setHistoryView]     = useState('timeline'); // 'timeline' | 'table'

  const API_BASE_URL = 'https://car.amar.co.jp/api';

  /* ── Fetch latest margin (dedicated endpoint) ── */
  const fetchCurrentMargin = async () => {
    setMarginLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/dashboard/car-price/get/latest/margin`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error('Failed to fetch latest margin');
      const data = await res.json();
      const margin = data.totalMargin;
      setCurrentMargin(margin);
      setNewMargin(String(margin));
      setMarginUpdatedDate(data.updatedDate ?? null);
    } catch (err) {
      setError(err.message);
      setCurrentMargin(20);
      setNewMargin('20');
    } finally {
      setMarginLoading(false);
    }
  };

  /* ── Fetch history ── */
  const fetchHistory = async () => {
    setHistoryLoading(true);
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/dashboard/car-price/getAll`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error('Failed to fetch history');
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];

      const mapped = list.map((item) => ({
        id:        item.marginPriceId,
        newMargin: item.totalMargin,
        updatedAt: item.updatedDate,
      }));
      setHistory(mapped);
    } catch (err) {
      setError(err.message);
      setHistory([
        { id: 1, newMargin: 20, updatedAt: '18-02-2026' },
        { id: 2, newMargin: 12, updatedAt: '18-02-2026' },
        { id: 3, newMargin: 20, updatedAt: '18-02-2026' },
      ]);
    } finally {
      setHistoryLoading(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrentMargin();
    fetchHistory();
  }, []);

  /* ── Validation ── */
  const validateInput = (val) => {
    const n = parseFloat(val);
    if (val === '')  return 'Margin value is required.';
    if (isNaN(n))    return 'Please enter a valid number.';
    if (n < 0)       return 'Margin cannot be negative.';
    if (n > 100)     return 'Margin cannot exceed 100%.';
    return '';
  };

  const handleSaveClick = () => {
    const err = validateInput(newMargin);
    if (err) { setInputError(err); return; }
    if (parseFloat(newMargin) === currentMargin) {
      setInputError('New margin is the same as current margin.');
      return;
    }
    setShowConfirmModal(true);
  };

  /* ── Updated: PUT /dashboard/car-price/update-margin ── */
  const handleConfirmSave = async () => {
    setSaving(true);
    const updatedMargin = parseFloat(newMargin);
    try {
      const res = await fetch(`${API_BASE_URL}/dashboard/car-price/update-margin`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ totalMargin: updatedMargin }),
      });

      // Treat 2xx responses as success; also tolerate 504 (gateway timeout)
      // since the server may process the update before the proxy times out.
      const isSuccess = res.ok || res.status === 504;
      if (!isSuccess) throw new Error(`Update failed (${res.status})`);

      // Try to parse JSON; fall back gracefully if the body is HTML / empty
      let savedMargin = updatedMargin;
      try {
        const contentType = res.headers.get('Content-Type') || '';
        if (contentType.includes('application/json')) {
          const saved = await res.json();
          savedMargin = saved.totalMargin ?? updatedMargin;
        }
      } catch (_) {
        // Non-JSON body (e.g. 504 HTML) – use the value we sent
      }

      setCurrentMargin(savedMargin);
      setNewMargin(String(savedMargin));
      setSuccessMsg(
        `✅ Margin updated to ${savedMargin}%. All inventory prices have been recalculated.`
      );
      await fetchHistory();
      await fetchCurrentMargin();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
      setShowConfirmModal(false);
      setTimeout(() => setSuccessMsg(''), 6000);
    }
  };

  // Handles both "DD-MM-YYYY" from API and ISO strings
  const formatDate = (val) => {
    if (!val) return 'N/A';
    let date;
    if (/^\d{2}-\d{2}-\d{4}$/.test(val)) {
      const [dd, mm, yyyy] = val.split('-');
      date = new Date(`${yyyy}-${mm}-${dd}`);
    } else {
      date = new Date(val);
    }
    return isNaN(date) ? val : date.toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  };

  const marginDiff = !marginLoading && newMargin !== ''
    ? (parseFloat(newMargin) - currentMargin).toFixed(2)
    : null;

  /* ── Sort history descending by id ── */
  const sortedHistory = [...history].sort((a, b) => b.id - a.id);

  /* ── Render ── */
  return (
    <div className="spm-wrapper">

      {/* ── Page Header ── */}
      <div className="spm-page-header">
        <div className="spm-page-header-left">
          <div className="spm-breadcrumb">Settings &rsaquo; Pricing</div>
          <h1 className="spm-page-title">
            <span className="spm-title-icon">🏷️</span>
            Set Price Margin
          </h1>
          <p className="spm-page-desc">
            Control the global profit margin applied across all car listings in inventory.
          </p>
        </div>
        <div className="spm-header-badge">
          <span className="spm-live-dot" />
          Live
        </div>
      </div>

      {/* ── Success Banner ── */}
      {successMsg && (
        <div className="spm-success-banner">
          <span>{successMsg}</span>
          <button onClick={() => setSuccessMsg('')}>×</button>
        </div>
      )}

      {/* ── Top Cards Row ── */}
      <div className="spm-cards-row">

        {/* Gauge Card */}
        <div className="spm-gauge-card">
          <div className="spm-gauge-ring-wrap">
            <svg viewBox="0 0 120 120" className="spm-gauge-svg">
              <circle cx="60" cy="60" r="50" fill="none" stroke="#f1f1f1" strokeWidth="10" />
              <circle
                cx="60" cy="60" r="50" fill="none"
                stroke="url(#redGrad)" strokeWidth="10"
                strokeDasharray={`${Math.min((currentMargin ?? 0) / 100 * 314, 314)} 314`}
                strokeLinecap="round"
                transform="rotate(-90 60 60)"
                style={{ transition: 'stroke-dasharray 0.8s ease' }}
              />
              <defs>
                <linearGradient id="redGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#ff4444" />
                  <stop offset="100%" stopColor="#cc0000" />
                </linearGradient>
              </defs>
            </svg>
            <div className="spm-gauge-center">
              {loading ? (
                <span className="spm-gauge-loading">…</span>
              ) : (
                <>
                  <span className="spm-gauge-value">{currentMargin ?? 0}</span>
                  <span className="spm-gauge-unit">%</span>
                </>
              )}
            </div>
          </div>
          <div className="spm-gauge-label">Current Margin</div>
          <div className="spm-gauge-sublabel">Applied to all inventory vehicles</div>
        </div>

        {/* Edit Card */}
        <div className="spm-edit-card">
          <div className="spm-edit-card-header">
            <span className="spm-edit-card-icon">✏️</span>
            <h3>Update Margin</h3>
          </div>

          <div className="spm-editing-area">
            <label className="spm-input-label">New Margin (%)</label>
            <div className="spm-input-row">
              <input
                type="number"
                className={`spm-margin-input ${inputError ? 'spm-input-error' : ''}`}
                value={newMargin}
                min="0" max="100" step="0.1"
                placeholder="e.g. 15.0"
                disabled={marginLoading}
                onChange={(e) => {
                  setNewMargin(e.target.value);
                  setInputError(validateInput(e.target.value));
                }}
              />
              <span className="spm-input-pct">%</span>
            </div>
            {inputError && <p className="spm-input-err-msg">⚠️ {inputError}</p>}

            {marginDiff !== null && !inputError && newMargin !== '' && (
              <div className={`spm-diff-chip ${parseFloat(marginDiff) > 0 ? 'spm-diff-up' : 'spm-diff-down'}`}>
                {parseFloat(marginDiff) > 0 ? '▲' : '▼'} {Math.abs(marginDiff)}% from current
              </div>
            )}

            <div className="spm-action-row">
              <button className="spm-btn-save" onClick={handleSaveClick} disabled={marginLoading}>
                💾 Save Changes
              </button>
            </div>

            {marginUpdatedDate && (
              <div className="spm-margin-date-info">
                <span className="spm-margin-date-label">📅 Margin set on</span>
                <span className="spm-margin-date-value">{formatDate(marginUpdatedDate)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Info Card */}
        <div className="spm-info-card">
          <div className="spm-info-card-header">
            <span>ℹ️</span>
            <h3>How it works</h3>
          </div>
          <ul className="spm-info-list">
            <li><span className="spm-info-bullet" />Margin is added on top of the <strong>base car price</strong>.</li>
            <li><span className="spm-info-bullet" />Saving will <strong>instantly recalculate</strong> all listed prices.</li>
            <li><span className="spm-info-bullet" />Historical changes are logged in the audit table below.</li>
            <li><span className="spm-info-bullet" />Example: Base <strong>$10,000</strong> + 12.5% = <strong>$11,250</strong> listed.</li>
          </ul>
          {currentMargin !== null && (
            <div className="spm-example-box">
              <span className="spm-example-label">Live Example</span>
              <span className="spm-example-val">
                $10,000 → ${(10000 * (1 + currentMargin / 100)).toLocaleString()}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── History Section ── */}
      <div className="spm-history-section">
        <div className="spm-history-header">
          <div className="spm-history-header-left">
            <h2 className="spm-history-title">📋 Margin Update History</h2>
            <p className="spm-history-sub">Audit log of all price margin changes</p>
          </div>
          <div className="spm-history-header-right">
            <span className="spm-history-count">{history.length} records</span>
            <div className="spm-view-toggle">
              <button
                className={`spm-toggle-btn ${historyView === 'timeline' ? 'active' : ''}`}
                onClick={() => setHistoryView('timeline')}
              >
                ⏱ Timeline
              </button>
              <button
                className={`spm-toggle-btn ${historyView === 'table' ? 'active' : ''}`}
                onClick={() => setHistoryView('table')}
              >
                ☰ Table
              </button>
            </div>
          </div>
        </div>

        {historyLoading ? (
          <div className="spm-history-loading">
            <div className="spm-spinner" />
            <p>Loading history…</p>
          </div>
        ) : history.length === 0 ? (
          <div className="spm-history-empty">
            <span>🗂️</span>
            <p>No margin history found.</p>
          </div>
        ) : historyView === 'timeline' ? (
          <TimelineView sortedHistory={sortedHistory} formatDate={formatDate} />
        ) : (
          <TableView sortedHistory={sortedHistory} formatDate={formatDate} />
        )}
      </div>

      {/* ── Confirm Save Modal ── */}
      {showConfirmModal && (
        <div className="spm-modal-overlay">
          <div className="spm-modal">
            <div className="spm-modal-icon-wrap">
              <div className="spm-modal-icon">🔄</div>
            </div>
            <h3 className="spm-modal-title">Confirm Margin Update</h3>
            <p className="spm-modal-body">
              You are about to change the global price margin from{' '}
              <strong>{currentMargin}%</strong> to <strong>{newMargin}%</strong>.
            </p>
            <div className="spm-modal-alert">
              ⚠️ This will <strong>immediately recalculate and update listed prices</strong> for{' '}
              <strong>all cars in the inventory</strong>. This action cannot be undone.
            </div>
            <div className="spm-modal-actions">
              <button
                className="spm-modal-cancel"
                onClick={() => setShowConfirmModal(false)}
                disabled={saving}
              >
                ✕ Cancel
              </button>
              <button
                className="spm-modal-confirm"
                onClick={handleConfirmSave}
                disabled={saving}
              >
                {saving ? (
                  <><span className="spm-btn-spinner" /> Updating…</>
                ) : (
                  '✅ Yes, Update All Prices'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SetPriceMargin;